import React from 'react';
import { ThreeDotsVertical } from 'react-bootstrap-icons';
import moment from 'moment';
import { usePopup } from '../../../../../hooks';
import { CareProgressBadgeType, type CareProgressRowData } from '../types';
import CareProgressBadge from '../components/CareProgressBadge';
import AdditionalInsightsPopup from '../../../../ProfileBar/AdditionalInsightsPopup/AdditionalInsightsPopup';

interface DCFormatterProps {
  id: string;
  scriptWrittenData: CareProgressRowData[];
  dischargeData: CareProgressRowData[];
}

type EventDates = {
  inProgress: string[];
  completed: string[];
};

const DCFormatter: React.FC<DCFormatterProps> = ({
  id,
  scriptWrittenData,
  dischargeData,
}) => {
  const { openPopup, openPopupId, closePopup } = usePopup();
  const scriptWrittenEventDates: EventDates = React.useMemo(() => {
    const dates: EventDates = {
      inProgress: [],
      completed: [],
    };
    scriptWrittenData.forEach((event) => {
      const isProgress =
        event.detail.status === CareProgressBadgeType.IN_PROGRESS;
      const isCompleted =
        event.detail.status === CareProgressBadgeType.COMPLETED;
      if (isProgress) {
        dates.inProgress.push(moment(event.event_date).format('Do MMM YY'));
      } else if (isCompleted) {
        dates.completed.push(moment(event.event_date).format('Do MMM YY'));
      }
    });
    return dates;
  }, [scriptWrittenData]);

  const dischargeEventDates: EventDates = React.useMemo(() => {
    const dates: EventDates = {
      inProgress: [],
      completed: [],
    };
    dischargeData.forEach((event) => {
      const isProgress =
        event.detail.status === CareProgressBadgeType.IN_PROGRESS;
      const isCompleted =
        event.detail.status === CareProgressBadgeType.COMPLETED;
      if (isProgress) {
        dates.inProgress.push(moment(event.event_date).format('Do MMM YY'));
      } else if (isCompleted) {
        dates.completed.push(moment(event.event_date).format('Do MMM YY'));
      }
    });
    return dates;
  }, [dischargeData]);

  const renderedScriptWrittenBadges: React.ReactElement = React.useMemo(() => {
    if (scriptWrittenData.length === 0) {
      return (
        <CareProgressBadge
          id={`script-written-${id}`}
          type={CareProgressBadgeType.NO_DOCUMENT}
          columnName="script written"
        />
      );
    }
    return (
      <>
        {scriptWrittenEventDates.completed.length > 0 && (
          <CareProgressBadge
            id={`script-written-${id}`}
            type={CareProgressBadgeType.COMPLETED}
            completedDates={scriptWrittenEventDates.completed}
            columnName="script written"
          />
        )}
        {scriptWrittenEventDates.inProgress.length > 0 && (
          <CareProgressBadge
            id={`script-written-${id}`}
            type={CareProgressBadgeType.IN_PROGRESS}
            startedDates={scriptWrittenEventDates.inProgress}
            columnName="script written"
          />
        )}
      </>
    );
  }, [scriptWrittenEventDates, id, scriptWrittenData]);

  const renderedDischargeBadges: React.ReactElement = React.useMemo(() => {
    if (dischargeData.length === 0) {
      return (
        <CareProgressBadge
          id={`discharge-${id}`}
          type={CareProgressBadgeType.NO_DOCUMENT}
          columnName="D/C"
        />
      );
    }
    return (
      <>
        {dischargeEventDates.completed.length > 0 && (
          <CareProgressBadge
            id={`discharge-${id}`}
            type={CareProgressBadgeType.COMPLETED}
            completedDates={dischargeEventDates.completed}
            columnName="D/C"
          />
        )}
        {dischargeEventDates.inProgress.length > 0 && (
          <CareProgressBadge
            id={id}
            type={CareProgressBadgeType.IN_PROGRESS}
            startedDates={dischargeEventDates.inProgress}
            columnName="D/C"
          />
        )}
      </>
    );
  }, []);

  const onOpenAdditionalInsights = (e: React.MouseEvent) => {
    e.stopPropagation();
    openPopup(id);
  };

  return (
    <>
      <div className="d-flex gap-1 align-items-center">
        <div className="d-flex gap-1">
          {renderedDischargeBadges}
          {renderedScriptWrittenBadges}
        </div>
        <button onClick={onOpenAdditionalInsights}>
          <ThreeDotsVertical />
        </button>
      </div>
      <AdditionalInsightsPopup open={openPopupId === id} onHide={closePopup} />
    </>
  );
};

export default DCFormatter;
